var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from "react";
import { getCalendar } from "./timeContextHelpers/GetCalendar";
import { getMonthName } from "./timeContextHelpers/GetMonthName";
import { getSeasonFromDate } from "./timeContextHelpers/GetSeasonFromDate";
var TimeManagementContext = createContext(undefined);
export function TimeManagementContextProvider(props) {
    var limitToCurrentSeason = props.limitToCurrentSeason, timeRangeStart = props.timeRangeStart, timeRangeEnd = props.timeRangeEnd;
    var _a = useState(""), monthNameLong = _a[0], setMonthNameLong = _a[1];
    var _b = useState(""), monthNameShort = _b[0], setMonthNameShort = _b[1];
    var _c = useState([[]]), weeks = _c[0], setWeeks = _c[1];
    var _d = useState([]), matches = _d[0], setMatches = _d[1];
    var _e = useState(new Date().getMonth()), month = _e[0], setMonth = _e[1];
    var _f = useState(new Date().getFullYear()), year = _f[0], setYear = _f[1];
    var _g = useState(getSeasonFromDate(new Date())), season = _g[0], setSeason = _g[1];
    var _h = useState(undefined), selectedDate = _h[0], setSelectedDate = _h[1];
    var _j = useState(undefined), selectedMatch = _j[0], setSelectedMatch = _j[1];
    var _k = useState(props.timeRangeStart), earlyLimit = _k[0], setEarlyLimit = _k[1];
    var _l = useState(props.timeRangeEnd), lateLimit = _l[0], setLateLimit = _l[1];
    var _m = useState(true), canGoIntoPast = _m[0], setCanGoIntoPast = _m[1];
    var _o = useState(true), canGoIntoFuture = _o[0], setCanGoIntoFuture = _o[1];
    useEffect(function () {
        props.getterFunction(season)
            .then(function (payload) {
            setMatches(payload.matches);
        });
    }, [season]);
    useEffect(function () {
        if (limitToCurrentSeason) {
            var currentDate = new Date();
            if (currentDate.getMonth() < 5) {
                setEarlyLimit(new Date(currentDate.getFullYear() - 1, 5)); // Earliest allowed date is 1 June of previous year
                setLateLimit(new Date(currentDate.getFullYear(), 4, 31, 23, 59, 59)); // Latest allowed date is 31 May of current year
            }
            else {
                setEarlyLimit(new Date(currentDate.getFullYear(), 5)); // Earliest allowed date is 1 June of current year
                setLateLimit(new Date(currentDate.getFullYear() + 1, 4, 31, 23, 59, 59)); // Latest allowed date is 31 May of next year
            }
        }
        else {
            setEarlyLimit(timeRangeStart);
            setLateLimit(timeRangeEnd);
        }
    }, [limitToCurrentSeason, timeRangeStart, timeRangeEnd]);
    useEffect(function () {
        setWeeks(getCalendar(month, year));
        setMonthNameLong(getMonthName(month, true));
        setMonthNameShort(getMonthName(month, false));
        setSeason(getSeasonFromDate(new Date(year, month)));
    }, [month, year]);
    useEffect(function () {
        setCanGoIntoPast(validateTemporalLimits(month - 1, year));
        setCanGoIntoFuture(validateTemporalLimits(month + 1, year));
    }, [month, year, earlyLimit, lateLimit]);
    var validateTemporalLimits = function (proposedMonth, proposedYear) {
        var proposedDate = new Date(proposedYear, proposedMonth, 1);
        if (earlyLimit) {
            if (proposedDate < earlyLimit)
                return false;
        }
        if (lateLimit) {
            if (proposedDate > lateLimit)
                return false;
        }
        return true;
    };
    /**
     * Handles navigation to the previous month, includes validation if context has any limits applied
     */
    var handleGoIntoPast = function () {
        if (month === 0) {
            if (validateTemporalLimits(11, year - 1)) {
                setMonth(11);
                setYear(year - 1);
            }
        }
        else {
            if (validateTemporalLimits(month - 1, year)) {
                setMonth(month - 1);
            }
        }
    };
    /**
     * Handles navigation to the next month, includes validation if context has any limits applied
     */
    var handleGoIntoFuture = function () {
        if (month === 11) {
            if (validateTemporalLimits(0, year + 1)) {
                setMonth(0);
                setYear(year + 1);
            }
        }
        else {
            if (validateTemporalLimits(month + 1, year))
                setMonth(month + 1);
        }
    };
    /**
     * Handles navigation to a specific month, includes validation if context has any limits applied
     * @param year
     * @param month
     */
    var handleGoToMonth = function (year, month) {
        if (!validateTemporalLimits(month, year))
            return;
        setYear(year);
        setMonth(month);
    };
    return (_jsx(TimeManagementContext.Provider, __assign({ value: {
            monthNameLong: monthNameLong,
            monthNameShort: monthNameShort,
            weeks: weeks,
            matches: matches,
            month: month,
            year: year,
            selectedDate: selectedDate,
            selectedMatch: selectedMatch,
            setSelectedDate: setSelectedDate,
            setSelectedMatch: setSelectedMatch,
            handleGoIntoPast: handleGoIntoPast,
            handleGoIntoFuture: handleGoIntoFuture,
            handleGoToMonth: handleGoToMonth,
            canGoIntoPast: canGoIntoPast,
            canGoIntoFuture: canGoIntoFuture,
        } }, { children: props.children })));
}
export function useTimeManagementContext() {
    var context = useContext(TimeManagementContext);
    if (context === undefined) {
        throw new Error("useTimeManagementContext must be used within a TimeManagementContextProvider");
    }
    return context;
}
